import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { useAuth } from '../context/AuthContext';
import { CpaSendItem } from './CpaShareHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EmailToCpaDialog = ({
  advisorId, advisorName, sends = [], onSent,
  sendUrl = `${API}/chat/history/email`, title = 'Send this chat to your CPA', description, triggerTestId = 'email-cpa-btn',
}) => {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [toEmail, setToEmail] = useState('');
  const [note, setNote] = useState('');
  const [saveAsCpa, setSaveAsCpa] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setToEmail(user?.cpa_email || '');
      setSaveAsCpa(!user?.cpa_email);
    }
  }, [open, user?.cpa_email]);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    const shouldSave = saveAsCpa && toEmail.trim().toLowerCase() !== user?.cpa_email;
    try {
      const { data } = await axios.post(sendUrl, {
        ...(advisorId ? { advisor_id: advisorId } : {}), to_email: toEmail.trim(), note: note.trim() || null, save_as_cpa: shouldSave,
      }, { withCredentials: true });
      toast.success(data.message);
      setOpen(false);
      setNote('');
      if (shouldSave) refreshUser?.();
      onSent?.();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : error.response?.status === 422 ? 'Please enter a valid email address' : 'Could not send email');
    } finally {
      setSending(false);
    }
  };

  const showSave = toEmail.trim() && toEmail.trim().toLowerCase() !== user?.cpa_email;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="border-stone-300 text-stone-700 hover:bg-stone-100" data-testid={triggerTestId}>
          <Mail className="w-4 h-4 mr-2" /> Email to CPA
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="email-cpa-dialog">
        <form onSubmit={send}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {description || `Your conversation with ${advisorName} is attached as a PDF.`} Replies go straight to {user?.email}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <Label htmlFor="cpa-email">CPA's email</Label>
              <Input id="cpa-email" type="email" required placeholder="cpa@example.com" value={toEmail}
                onChange={(e) => setToEmail(e.target.value)} data-testid="email-cpa-to-input" />
              {user?.cpa_name && toEmail.trim().toLowerCase() === user?.cpa_email && (
                <p className="text-xs text-stone-500" data-testid="email-cpa-saved-hint">Saved CPA: {user.cpa_name}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cpa-note">Note (optional)</Label>
              <Textarea id="cpa-note" rows={4} maxLength={2000} placeholder="Hi — could we go over this at our next meeting?"
                value={note} onChange={(e) => setNote(e.target.value)} data-testid="email-cpa-note-input" />
            </div>
            {showSave && (
              <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                <Checkbox checked={saveAsCpa} onCheckedChange={(v) => setSaveAsCpa(!!v)} data-testid="email-cpa-save-checkbox" />
                Save as my CPA's email
              </label>
            )}
            {sends.length > 0 && (
              <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2" data-testid="email-cpa-history">
                <p className="text-xs font-semibold text-stone-700">Previously sent ({sends.length})</p>
                <ul className="max-h-40 overflow-y-auto divide-y divide-stone-200">
                  {sends.map((s, i) => <CpaSendItem key={s.sent_at} send={s} index={i} />)}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="email-cpa-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={sending || !toEmail.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="email-cpa-send-btn">
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} Send PDF
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EmailToCpaDialog;
